/**
 * A guide document rendered in an iframe that grows to fit its content.
 *
 * Eight guides are complete HTML documents written into an iframe with
 * document.write. Each one sized itself to `calc(100vh - 49px)`, a viewport
 * height minus the bespoke 49px header that page happened to have. That works
 * only while nothing else is on the page: the iframe owns the whole viewport
 * and scrolls internally.
 *
 * It stops working the moment there is a site footer, because the outer page
 * then needs to scroll too, and the visitor gets two scrollbars with the
 * footer stranded below a full-height frame they have to scroll past twice.
 *
 * So the frame reports its own content height and the outer page scrolls
 * normally, the way it would if the guide were ordinary markup. The header and
 * footer then sit above and below it without any magic numbers, and nothing
 * has to know how tall the header is.
 */

import { useEffect, useRef, useState } from "react";
import { GUIDE_CSS_VARS, GUIDE_FONT_LINK } from "@/lib/guideTheme";
import { BUTTON_CSS, RADIUS_CSS } from "@/lib/buttonStyles";

export default function GuideFrame({
  html,
  title,
}: {
  html: string;
  title: string;
}) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(600);

  useEffect(() => {
    const iframe = ref.current;
    if (iframe == null) return;
    const doc = iframe.contentDocument ?? iframe.contentWindow?.document;
    if (doc == null) return;

    doc.open();
    doc.write(html);
    doc.close();

    /**
     * The guide's own <style> lives in the document we just wrote, so these
     * have to be appended after the write or they would be discarded with the
     * rest of the previous document.
     */
    const fonts = doc.createElement("div");
    fonts.innerHTML = GUIDE_FONT_LINK;
    while (fonts.firstChild) doc.head.appendChild(fonts.firstChild);

    /**
     * Appended last so they win on equal specificity.
     *
     * The palette comes first because the button rules reference it, then the
     * buttons and the corner radii, which are the two things that still made a
     * guide read as a different site once the colours matched. The documents
     * ship `border-radius: 2px` on their containers; everything else on the
     * site is built on 8px.
     */
    const vars = doc.createElement("style");
    vars.textContent = GUIDE_CSS_VARS + BUTTON_CSS + RADIUS_CSS;
    doc.head.appendChild(vars);

    /**
     * Content height, remeasured whenever it changes. These documents have
     * collapsible steps and lazily loaded images, so a single measurement
     * after write is read too early and leaves the page cut off.
     */
    const measure = () => {
      const body = doc.body;
      if (body == null) return;
      const next = Math.max(
        body.scrollHeight,
        doc.documentElement?.scrollHeight ?? 0
      );
      // Only grow past a sane floor; a transient 0 during layout would
      // otherwise collapse the frame to nothing.
      if (next > 0) setHeight(next);
    };

    measure();

    const observer = new ResizeObserver(measure);
    if (doc.body) observer.observe(doc.body);

    // Catches expand/collapse inside the guide, which changes height without
    // resizing the body box in every browser.
    const mutation = new MutationObserver(measure);
    if (doc.body) {
      mutation.observe(doc.body, {
        subtree: true,
        childList: true,
        attributes: true,
      });
    }

    const onLoad = () => measure();
    iframe.contentWindow?.addEventListener("load", onLoad);
    const timers = [250, 1000, 2500].map(ms => window.setTimeout(measure, ms));

    return () => {
      observer.disconnect();
      mutation.disconnect();
      iframe.contentWindow?.removeEventListener("load", onLoad);
      timers.forEach(window.clearTimeout);
    };
  }, [html]);

  return (
    <iframe
      ref={ref}
      title={title}
      // scrolling is off because the outer page does the scrolling now.
      scrolling="no"
      className="w-full border-0 block"
      style={{ height }}
      sandbox="allow-scripts allow-same-origin allow-popups"
    />
  );
}
